import {
  Breadcrumb,
  Button,
  Divider,
  Form,
  Input,
  Layout,
  notification,
  Result,
  Skeleton,
  theme,
  Space
} from "antd";
import { Content } from "antd/es/layout/layout";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { 
  apiGroupsDetail, 
  apiGroupsCreate, 
  apiGroupsUpdate, 
  apiGroupsPermissions 
} from "../../../../api/groups";
import "./GroupsDetails.scss";
import { EditOutlined, SaveOutlined, CloseOutlined } from "@ant-design/icons";
import PermissionsSelector from "../PermissionsSelector/PermissionsSelector";

const GroupsDetails = () => {
  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();
  const navigate = useNavigate();
  const { id } = useParams();
  const [form] = Form.useForm();

  // Determine mode based on URL and params
  const isCreateMode = id === 'criar' || !id;
  const [isEditMode, setEditMode] = useState(false)

  const [groupData, setGroupData] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [permissionsLoading, setPermissionsLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeError, setActiveError] = useState(false);
  const [errorStatus, setErrorStatus] = useState("");

  const renderError = (status) => {
    if (activeError) {
      return (
        <Result
          status={status === "500" ? "500" : "404"}
          title={status}
          subTitle={
            status === "500"
              ? "Ocorreu um erro no servidor, tente novamente mais tarde."
              : "Ocorreu um erro, tente novamente mais tarde."
          }
          extra={
            <Button type="primary" onClick={() => navigate("/perfis")}>
              Voltar para Perfis
            </Button>
          }
        />
      );
    }
  };

  // Load permissions
  useEffect(() => {
    const fetchPermissions = async () => {
      setPermissionsLoading(true);
      try {
        const permissionsData = await apiGroupsPermissions();
        setPermissions(permissionsData);
      } catch (error) {
        notification.error({
          message: "Erro",
          description: "Erro ao carregar permissões.",
          placement: "topRight",
        });
      } finally {
        setPermissionsLoading(false);
      }
    };

    fetchPermissions();
  }, []);

  // Load group data for view/edit modes
  useEffect(() => {
    const fetchGroupDetail = async () => {
      if (!isCreateMode && id) {
        try {
          setLoading(true);
          const data = await apiGroupsDetail(id);
          setGroupData(data);
          
          form.setFieldsValue({
            name: data.name,
            permissions: data.permissions?.map(p => p.id.toString()) || []
          });
        } catch (error) {
          setActiveError(true);
          if (error.status === 404) {
            setErrorStatus("404");
            notification.error({
              message: "Erro",
              description: "Perfil não encontrado.",
              placement: "topRight",
            });
          } else if (error.status === 500) {
            setErrorStatus("500");
            notification.error({
              message: "Erro",
              description: "Erro interno do servidor. Tente novamente mais tarde.",
              placement: "topRight",
            });
          } else {
            setErrorStatus("500");
            notification.error({
              message: "Erro",
              description: "Erro ao carregar os detalhes do perfil.",
              placement: "topRight",
            });
          }
        } finally {
          setLoading(false);
        }
      } else {
        setLoading(false);
      }
    };

    fetchGroupDetail();
  }, [id, isCreateMode, form]);

  const handleSave = async (values) => {
    setSaving(true);
    try {
      const data = {
        name: values.name,
        permissions: values.permissions || []
      };

      if (isCreateMode) {
        const result = await apiGroupsCreate(data);
        notification.success({
          message: "Sucesso",
          description: "Perfil criado com sucesso.",
          placement: "topRight",
        });
        navigate(`/perfis/${result.id}`);
      } else {
        await apiGroupsUpdate(id, data);
        notification.success({
          message: "Sucesso",
          description: "Perfil atualizado com sucesso.",
          placement: "topRight",
        });
        setEditMode(false);
      }
    } catch (error) {
      let errorMessage = isCreateMode 
        ? "Erro ao criar o perfil." 
        : "Erro ao atualizar o perfil.";
        console.log(error);
      
      // Handle specific error for duplicate group name
      if (error.status === 400 && error.message?.name && Array.isArray(error.message.name)) {
        const nameError = error.message.name[0];
        if (nameError && nameError.toLowerCase().includes("grupo com este nome já existe")) {
          errorMessage = "Já existe um perfil com este nome. Por favor, escolha um nome diferente.";
        }
      }
      
      notification.error({
        message: "Erro",
        description: errorMessage,
        placement: "topRight",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (isCreateMode) {
      navigate("/perfis");
    } else {
      navigate(`/perfis/${id}`);
    }
  };

  const getPageTitle = () => {
    if (isCreateMode) return "Criar Perfil";
    if (isEditMode) return "Editar Perfil";
    return groupData?.name || "Perfil";
  };

  const getBreadcrumbItems = () => {
    const items = [
      { title: "Sistema" },
      { title: "Perfis", onClick: () => navigate("/perfis"), className: "goBack" }
    ];

    if (isCreateMode) {
      items.push({ title: "Criar Perfil" });
    } else {
      items.push({ title: groupData?.name || "Perfil" });
      if (isEditMode) {
        items.push({ title: "Editar" });
      }
    }

    return items;
  };

  return (
    <div className="GroupsDetails">
      <Layout
        style={{
          padding: "1.25rem",
        }}
      >
        {activeError ? (
          renderError(errorStatus)
        ) : (
          <>
            <Breadcrumb
              style={{
                margin: "16px 0",
              }}
            >
              <div
                style={{
                  display: "flex",
                  width: "100%",
                  justifyContent: "space-between",
                  alignItems: "flex-end",
                }}
              >
                {loading && !isCreateMode ? (
                  <Skeleton active paragraph={{ rows: 1 }} />
                ) : (
                  <h2 className="title-page">
                    {getPageTitle()}
                  </h2>
                )}
                
                {/* Action buttons for view mode */}
                {!isCreateMode && !loading && (
                  <Space>
                    <Button 
                      type={!isEditMode? "default" : "primary"}
                      icon={<EditOutlined />}
                      onClick={() => setEditMode(!isEditMode)}
                    >
                      Editar
                    </Button>
                  </Space>
                )}
              </div>
              <Divider />
              {getBreadcrumbItems().map((item, index) => (
                <Breadcrumb.Item
                  key={index}
                  className={item.className}
                  onClick={item.onClick}
                >
                  {item.title}
                </Breadcrumb.Item>
              ))}
              
              {loading && !isCreateMode && (
                <Skeleton active paragraph={{ rows: 0 }} />
              )}
            </Breadcrumb>

            <Content
              style={{
                padding: 24,
                margin: 0,
                minHeight: 280,
                background: colorBgContainer,
                borderRadius: borderRadiusLG,
              }}
            >
              {loading && !isCreateMode ? (
                <Skeleton active paragraph={{ rows: 6 }} />
              ) : (
                <>
                  <div className="container-subtitle">
                    <h3 className="subtitle-page">
                      {isCreateMode ? "Informações do Novo Perfil" : 
                        "Editar Informações do Perfil"}
                    </h3>
                  </div>
                  <Divider />
                  
                  <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleSave}
                    initialValues={{
                      name: "",
                      permissions: []
                    }}
                  >
                    <div className="container-inputs">
                      <div className="container-left">
                        <Form.Item
                          label="Nome"
                          name="name"
                          rules={[
                            { required: true, message: 'Por favor, informe o nome do perfil!' },
                            { min: 2, message: 'O nome deve ter pelo menos 2 caracteres!' }
                          ]}
                          help={isEditMode && id === "1" ? 
                            "O nome do grupo padrão não pode ser alterado." : undefined}
                        >
                          <Input 
                           disabled={((!isCreateMode && !isEditMode) || saving) || (isEditMode && id === "1")}
                           placeholder="Digite o nome do perfil" 
                           />
                        </Form.Item>
                      </div>
                      <div className="container-right">
                        <Form.Item
                          label="Permissões"
                          name="permissions"
                        >
                          <PermissionsSelector
                            disabled={(!isCreateMode && !isEditMode) || saving}
                            loading={permissionsLoading}
                            permissions={permissions}
                          />
                        </Form.Item>
                      </div>
                    </div>
                    
                    <div className="form-actions">
                      <Space>
                        {isCreateMode || isEditMode ? (
                          <>
                          <Button 
                          type="primary" 
                          htmlType="submit" 
                          loading={saving}
                          icon={<SaveOutlined />}
                        >
                          {isCreateMode ? "Criar" : "Salvar"}
                        </Button>
                        <Button 
                          onClick={handleCancel}
                          icon={<CloseOutlined />}
                        >
                          Cancelar
                        </Button>
                          </>
                      ): <></>}
                      </Space>
                    </div>
                  </Form>
                </>
              )}
            </Content>
          </>
        )}
      </Layout>

    </div>
  );
};

export default GroupsDetails;